// pages/GiftSets.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import GiftHeroSlider from '../components/gifts/GiftHeroSlider';
import LuxuryCard from '../components/ui/LuxuryCard';
import {
    FiGift,
    FiStar,
    FiHeart,
    FiPackage,
    FiMessageCircle,
    FiBookOpen,
    FiChevronRight,
    FiShoppingBag,
    FiCalendar,
    FiMapPin,
    FiClock,
    FiCheck
} from 'react-icons/fi';
import {
    GiGemNecklace,
    GiRibbonMedal,
    GiPresent,
    GiCrystalBall,
    GiDiamondRing,
    GiGemini,
    GiDiamondHard
} from 'react-icons/gi';
import { IoDiamondOutline } from 'react-icons/io5';

const GiftSetsPage = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeCollection, setActiveCollection] = useState('all');
    const [hoveredGift, setHoveredGift] = useState(null);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const luxuryCollections = [
        {
            id: 'all',
            label: 'All Collections',
            icon: <IoDiamondOutline className="w-5 h-5" />,
            description: 'Complete curation of luxury gifting'
        },
        {
            id: 'signature',
            label: 'Signature Collections',
            icon: <GiDiamondRing className="w-6 h-6" />,
            description: 'Our most iconic, timeless collections'
        },
        {
            id: 'limited',
            label: 'Limited Editions',
            icon: <GiCrystalBall className="w-6 h-6" />,
            description: 'Exclusive, numbered collections'
        },
        {
            id: 'travel',
            label: 'Travel Luxe',
            icon: <FiMapPin className="w-5 h-5" />,
            description: 'Curated for the discerning traveler'
        },
        {
            id: 'corporate',
            label: 'Corporate Excellence',
            icon: <GiGemini className="w-6 h-6" />,
            description: 'Executive gifting at its finest'
        },
        {
            id: 'celebration',
            label: 'Celebration',
            icon: <GiDiamondHard className="w-6 h-6" />,
            description: 'For life\'s most precious moments'
        }
    ];

    const exclusiveGiftSets = [
        {
            id: 1,
            name: "The KROMAIC Signature Collection",
            price: 2850,
            originalPrice: 3400,
            description: "An exquisite curation of our finest formulations, presented in handcrafted walnut with 24k gold detailing. Limited to 50 pieces worldwide.",
            image: "/images/gift-set-1.jpg",
            collection: ["signature", "limited"],
            isNew: true,
            isLimited: true,
            edition: "1/50",
            features: [
                { icon: <GiDiamondHard className="w-4 h-4" />, text: "24k Gold Accents" },
                { icon: <FiCheck className="w-4 h-4" />, text: "Handcrafted Walnut" },
                { icon: <FiClock className="w-4 h-4" />, text: "Lifetime Refill Service" },
                { icon: <GiGemini className="w-4 h-4" />, text: "Personal Concierge" }
            ],
            includes: [
                "Supreme Serum No. 1 (50ml)",
                "Dew Cream Elixir (50ml)",
                "Golden Hour Essence (30ml)",
                "Velvet Cleansing Balm (100ml)",
                "Handcrafted Rose Quartz Gua Sha"
            ]
        },
        {
            id: 2,
            name: "L'Heure Dorée - Golden Hour",
            price: 1950,
            originalPrice: 2300,
            description: "Capture the magic of golden hour with this illuminating collection, presented in hand-blown crystal with gold leaf inlay.",
            image: "/images/gift-set-2.jpg",
            collection: ["signature", "celebration"],
            isNew: false,
            isLimited: true,
            edition: "Limited Edition",
            features: [
                { icon: <GiDiamondHard className="w-4 h-4" />, text: "Hand-blown Crystal" },
                { icon: <FiCheck className="w-4 h-4" />, text: "24k Gold Leaf" },
                { icon: <FiClock className="w-4 h-4" />, text: "Evening Ritual Focus" },
                { icon: <GiGemini className="w-4 h-4" />, text: "Illuminating Formulas" }
            ],
            includes: [
                "Golden Glow Serum (30ml)",
                "Moonlight Cream (50ml)",
                "Evening Elixir Oil (15ml)",
                "Silk Sleep Mask",
                "Crystal Roller"
            ]
        },
        {
            id: 3,
            name: "Botanical Treasury",
            price: 1650,
            originalPrice: 1950,
            description: "A discovery of our rarest botanical extracts, presented in sustainable bamboo with mother-of-pearl inlay.",
            image: "/images/gift-set-3.jpg",
            collection: ["signature"],
            isNew: true,
            isLimited: false,
            edition: "Sustainable Edition",
            features: [
                { icon: <GiDiamondHard className="w-4 h-4" />, text: "Mother-of-Pearl" },
                { icon: <FiCheck className="w-4 h-4" />, text: "Sustainable Bamboo" },
                { icon: <FiClock className="w-4 h-4" />, text: "Rare Botanicals" },
                { icon: <GiGemini className="w-4 h-4" />, text: "Carbon Neutral" }
            ],
            includes: [
                "Botanical Serum Trio (3x15ml)",
                "Herbal Infusion Cream (50ml)",
                "Flower Essence Mist (100ml)",
                "Bamboo Cleansing Cloth",
                "Botanical Guidebook"
            ]
        },
        {
            id: 4,
            name: "Voyage Privé - Travel Collection",
            price: 1250,
            originalPrice: 1500,
            description: "Essential luxury for the global citizen, presented in Italian leather with platinum hardware.",
            image: "/images/gift-set-4.jpg",
            collection: ["travel", "corporate"],
            isNew: false,
            isLimited: false,
            edition: "Travel Edition",
            features: [
                { icon: <GiDiamondHard className="w-4 h-4" />, text: "Italian Leather" },
                { icon: <FiCheck className="w-4 h-4" />, text: "Platinum Hardware" },
                { icon: <FiClock className="w-4 h-4" />, text: "TSA Approved" },
                { icon: <GiGemini className="w-4 h-4" />, text: "30-Day Ritual" }
            ],
            includes: [
                "Travel Serum Set (5x10ml)",
                "Compact Cream (30ml)",
                "Mini Cleansing Oil (50ml)",
                "Leather Travel Case",
                "Global Voltage Adapter"
            ]
        },
        {
            id: 5,
            name: "Collection Privée - Ultimate Luxury",
            price: 6500,
            originalPrice: 7800,
            description: "Our most exclusive offering, featuring bespoke formulations in a presentation of rare wood and white gold.",
            image: "/images/gift-set-5.jpg",
            collection: ["limited", "celebration"],
            isNew: false,
            isLimited: true,
            edition: "1/25",
            features: [
                { icon: <GiDiamondHard className="w-4 h-4" />, text: "White Gold Accents" },
                { icon: <FiCheck className="w-4 h-4" />, text: "Rare Ebony Wood" },
                { icon: <FiClock className="w-4 h-4" />, text: "Bespoke Formulations" },
                { icon: <GiGemini className="w-4 h-4" />, text: "Personal Perfumer" }
            ],
            includes: [
                "Bespoke Serum (50ml)",
                "Custom Cream (50ml)",
                "Personal Fragrance (100ml)",
                "White Gold Tools",
                "Year of Concierge Service"
            ]
        },
        {
            id: 6,
            name: "Morning Ritual - Platinum Collection",
            price: 2200,
            originalPrice: 2650,
            description: "An energizing morning ritual in Swiss-made packaging with platinum detailing.",
            image: "/images/gift-set-6.jpg",
            collection: ["signature"],
            isNew: true,
            isLimited: false,
            edition: "Platinum Edition",
            features: [
                { icon: <GiDiamondHard className="w-4 h-4" />, text: "Platinum Detailing" },
                { icon: <FiCheck className="w-4 h-4" />, text: "Swiss-Made Case" },
                { icon: <FiClock className="w-4 h-4" />, text: "Morning Focus" },
                { icon: <GiGemini className="w-4 h-4" />, text: "Energizing Formulas" }
            ],
            includes: [
                "Vitamin C Elixir (30ml)",
                "Morning Dew Cream (50ml)",
                "Eye Revive Serum (15ml)",
                "Platinum Facial Roller",
                "Morning Ritual Guide"
            ]
        },
        {
            id: 7,
            name: "Executive Reserve",
            price: 3800,
            originalPrice: 4500,
            description: "Professional-grade luxury for boardroom success, in carbon fiber with titanium accents.",
            image: "/images/gift-set-7.jpg",
            collection: ["corporate", "travel"],
            isNew: false,
            isLimited: false,
            edition: "Executive Edition",
            features: [
                { icon: <GiDiamondHard className="w-4 h-4" />, text: "Carbon Fiber" },
                { icon: <FiCheck className="w-4 h-4" />, text: "Titanium Accents" },
                { icon: <FiClock className="w-4 h-4" />, text: "Professional Grade" },
                { icon: <GiGemini className="w-4 h-4" />, text: "Boardroom Ready" }
            ],
            includes: [
                "Executive Serum (50ml)",
                "24-Hour Cream (50ml)",
                "Shave Formula (100ml)",
                "Carbon Fiber Case",
                "Monogramming Service"
            ]
        },
        {
            id: 8,
            name: "Célébration Royale",
            price: 4250,
            originalPrice: 5100,
            description: "A festive masterpiece featuring diamond dust and Swarovski crystal embellishments.",
            image: "/images/gift-set-8.jpg",
            collection: ["celebration", "limited"],
            isNew: true,
            isLimited: true,
            edition: "Holiday 2024",
            features: [
                { icon: <GiDiamondHard className="w-4 h-4" />, text: "Diamond Dust" },
                { icon: <FiCheck className="w-4 h-4" />, text: "Swarovski Crystals" },
                { icon: <FiClock className="w-4 h-4" />, text: "Festive Scents" },
                { icon: <GiGemini className="w-4 h-4" />, text: "Limited Release" }
            ],
            includes: [
                "Festive Serum (30ml)",
                "Celebration Cream (50ml)",
                "Sparkling Body Oil (200ml)",
                "Crystal-Encrusted Tools",
                "Personalized Ornament"
            ]
        }
    ];

    const luxuryExperiences = [
        {
            icon: <GiDiamondHard className="w-10 h-10" />,
            title: "Bespoke Personalization",
            description: "Each collection can be personalized with monogramming, custom scent profiles, and tailored formulations.",
            gradient: "bg-gradient-to-br from-champagne-gold/20 via-champagne-gold/10 to-transparent"
        },
        {
            icon: <FiPackage className="w-10 h-10" />,
            title: "White Glove Delivery",
            description: "Every order receives our signature white glove delivery service, complete with personal setup assistance.",
            gradient: "bg-gradient-to-br from-deep-charcoal/20 via-deep-charcoal/10 to-transparent"
        },
        {
            icon: <FiMessageCircle className="w-10 h-10" />,
            title: "Concierge Service",
            description: "24/7 access to our luxury concierge for gift recommendations, personalization, and global shipping.",
            gradient: "bg-gradient-to-br from-muted-sage/20 via-muted-sage/10 to-transparent"
        },
        {
            icon: <FiCalendar className="w-10 h-10" />,
            title: "Scheduled Refills",
            description: "Automated luxury refill service ensures your favorite formulations are always replenished.",
            gradient: "bg-gradient-to-br from-champagne-gold/20 via-champagne-gold/10 to-transparent"
        }
    ];

    const filteredGiftSets = activeCollection === 'all'
        ? exclusiveGiftSets
        : exclusiveGiftSets.filter(set => set.collection.includes(activeCollection));

    return (
        <div className="min-h-screen bg-gradient-to-b from-pearl-white via-soft-ivory to-nude-beige/30">
            {/* Fixed Navbar */}
            <div
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
                    isScrolled
                        ? 'bg-white/95 backdrop-blur-xl shadow-soft border-b border-nude-beige/30'
                        : 'bg-transparent'
                }`}
            >
                <Navbar />
            </div>

            {/* Gift Hero Slider Section */}
            <section id="hero" className="relative w-full">
                <GiftHeroSlider />
            </section>

            {/* Luxury Collections Navigation */}
            <section id="collections-nav" className="section-spacing">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="max-w-6xl mx-auto"
                    >
                        <LuxuryCard className="p-8 backdrop-blur-sm" glow>
                            <div className="text-center mb-12">
                                <h2 className="font-cormorant text-4xl text-deep-charcoal mb-4">
                                    Select Your Luxury Collection
                                </h2>
                                <p className="font-poppins text-charcoal-60">
                                    Navigate our exclusive categories of luxury gifting
                                </p>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                {luxuryCollections.map((collection) => (
                                    <motion.button
                                        key={collection.id}
                                        whileHover={{ scale: 1.02, y: -4 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => setActiveCollection(collection.id)}
                                        className={`group relative p-6 rounded-2xl transition-all duration-500 ${
                                            activeCollection === collection.id
                                                ? 'bg-gradient-to-br from-deep-charcoal to-charcoal-80 shadow-2xl'
                                                : 'bg-white/80 hover:bg-white shadow-soft'
                                        }`}
                                    >
                                        {/* Active State Glow */}
                                        {activeCollection === collection.id && (
                                            <div className="absolute inset-0 bg-gradient-to-br from-champagne-gold/10 to-transparent rounded-2xl" />
                                        )}

                                        <div className="relative z-10">
                                            <div className={`flex items-center justify-center w-12 h-12 rounded-xl mb-4 mx-auto ${
                                                activeCollection === collection.id
                                                    ? 'bg-gradient-to-br from-champagne-gold to-champagne-gold/70 text-white'
                                                    : 'bg-gradient-to-br from-champagne-gold/10 to-muted-sage/10 text-deep-charcoal'
                                            }`}>
                                                {collection.icon}
                                            </div>

                                            <h3 className={`font-cormorant text-lg mb-2 ${
                                                activeCollection === collection.id ? 'text-white' : 'text-deep-charcoal'
                                            }`}>
                                                {collection.label}
                                            </h3>

                                            <p className={`font-poppins text-xs ${
                                                activeCollection === collection.id ? 'text-charcoal-40' : 'text-charcoal-60'
                                            }`}>
                                                {collection.description}
                                            </p>
                                        </div>

                                        {/* Hover Indicator */}
                                        <div className={`absolute bottom-4 left-1/2 transform -translate-x-1/2 w-8 h-0.5 rounded-full transition-all duration-300 ${
                                            activeCollection === collection.id
                                                ? 'bg-champagne-gold'
                                                : 'bg-transparent group-hover:bg-nude-beige'
                                        }`} />
                                    </motion.button>
                                ))}
                            </div>
                        </LuxuryCard>
                    </motion.div>
                </div>
            </section>

            {/* Exclusive Gift Sets Grid */}
            <section id="gift-sets" className="section-spacing">
                <div className="container-luxury">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeCollection}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="mb-16 text-center">
                                <div className="inline-flex items-center gap-4 mb-6">
                                    <GiGemNecklace className="w-8 h-8 text-champagne-gold" />
                                    <span className="font-poppins text-sm uppercase tracking-[0.2em] text-champagne-gold">
                    {luxuryCollections.find(c => c.id === activeCollection)?.label}
                  </span>
                                    <GiGemNecklace className="w-8 h-8 text-champagne-gold" />
                                </div>

                                <h2 className="font-playfair text-5xl md:text-6xl text-deep-charcoal mb-6">
                                    Curated Excellence
                                </h2>
                                <p className="font-poppins text-xl text-charcoal-60 max-w-3xl mx-auto">
                                    Each collection represents the pinnacle of luxury craftsmanship and formulation excellence
                                </p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {filteredGiftSets.map((gift, index) => (
                                    <motion.div
                                        key={gift.id}
                                        initial={{ opacity: 0, y: 60 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.8, delay: index * 0.1 }}
                                        whileHover={{ y: -8 }}
                                        onMouseEnter={() => setHoveredGift(gift.id)}
                                        onMouseLeave={() => setHoveredGift(null)}
                                        className="group relative"
                                    >
                                        <LuxuryCard glow className="overflow-hidden h-full" hoverable>
                                            {/* Edition Badge */}
                                            <div className="absolute top-6 left-6 z-20">
                                                <div className="bg-gradient-to-r from-deep-charcoal to-charcoal-80 text-white
                          px-4 py-2 rounded-full flex items-center gap-2"
                                                >
                                                    <IoDiamondOutline className="w-4 h-4" />
                                                    <span className="font-poppins text-xs uppercase tracking-widest">
                            {gift.edition}
                          </span>
                                                </div>
                                            </div>

                                            {/* Price Tag */}
                                            <div className="absolute top-6 right-6 z-20">
                                                <div className="text-right">
                                                    <div className="font-playfair text-3xl text-champagne-gold">
                                                        ${gift.price}
                                                    </div>
                                                    <div className="font-poppins text-sm text-charcoal-40 line-through">
                                                        ${gift.originalPrice}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Image Container */}
                                            <div className="relative h-80 overflow-hidden">
                                                <div className="absolute inset-0 bg-gradient-to-br from-nude-beige/30 to-champagne-gold/10" />
                                                <div className="absolute inset-0 bg-gradient-to-t from-deep-charcoal/40 via-transparent to-transparent" />

                                                {/* Floating Elements */}
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64
                            border border-champagne-gold/10 rounded-full"
                                                />
                                            </div>

                                            {/* Content */}
                                            <div className="p-8">
                                                <div className="flex items-start justify-between mb-6">
                                                    <div>
                                                        <h3 className="font-cormorant text-3xl text-deep-charcoal mb-2">
                                                            {gift.name}
                                                        </h3>
                                                        <div className="flex items-center gap-2">
                                                            {gift.isNew && (
                                                                <span className="bg-muted-sage text-white px-3 py-1 rounded-full text-xs font-poppins">
                                  New Arrival
                                </span>
                                                            )}
                                                            {gift.isLimited && (
                                                                <span className="bg-gradient-to-r from-champagne-gold to-champagne-gold/80
                                  text-deep-charcoal px-3 py-1 rounded-full text-xs font-poppins">
                                  Limited Edition
                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <p className="font-poppins text-charcoal-60 mb-8 leading-relaxed">
                                                    {gift.description}
                                                </p>

                                                {/* Features */}
                                                <div className="grid grid-cols-2 gap-4 mb-8">
                                                    {gift.features.map((feature, idx) => (
                                                        <div key={idx} className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-champagne-gold/10
                                to-muted-sage/10 flex items-center justify-center text-champagne-gold">
                                                                {feature.icon}
                                                            </div>
                                                            <span className="font-poppins text-sm text-charcoal-60">
                                {feature.text}
                              </span>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Includes */}
                                                <div className="mb-8">
                                                    <h4 className="font-cormorant text-lg text-deep-charcoal mb-4">
                                                        Collection Includes:
                                                    </h4>
                                                    <div className="space-y-2">
                                                        {gift.includes.map((item, idx) => (
                                                            <div key={idx} className="flex items-center gap-3">
                                                                <FiChevronRight className="w-4 h-4 text-champagne-gold" />
                                                                <span className="font-poppins text-sm text-charcoal-60">
                                  {item}
                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* CTA */}
                                                <motion.button
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    className="w-full py-4 bg-gradient-to-r from-deep-charcoal to-charcoal-80
                            text-white font-poppins rounded-xl hover:shadow-xl transition-all duration-300
                            flex items-center justify-center gap-3 group/btn"
                                                >
                                                    <span>Request Exclusive Access</span>
                                                    <FiChevronRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                                                </motion.button>
                                            </div>
                                        </LuxuryCard>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>

            {/* Luxury Experiences */}
            <section id="experiences" className="section-spacing bg-gradient-to-b from-white/50 to-pearl-white/30">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="max-w-6xl mx-auto"
                    >
                        <div className="text-center mb-16">
                            <div className="inline-flex items-center gap-4 mb-6">
                                <div className="h-px w-12 bg-gradient-to-r from-transparent to-champagne-gold" />
                                <GiRibbonMedal className="w-8 h-8 text-champagne-gold" />
                                <div className="h-px w-12 bg-gradient-to-l from-transparent to-champagne-gold" />
                            </div>

                            <h2 className="font-playfair text-5xl md:text-6xl text-deep-charcoal mb-8">
                                Beyond the Collection
                            </h2>
                            <p className="font-poppins text-xl text-charcoal-60 max-w-3xl mx-auto">
                                The KROMAIC experience extends far beyond the products—it's a complete luxury journey
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {luxuryExperiences.map((experience, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    whileHover={{ y: -12 }}
                                    className={`${experience.gradient} rounded-3xl p-8 border border-white/50 
                    backdrop-blur-sm shadow-soft hover:shadow-lift transition-all duration-500`}
                                >
                                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/80 to-white/50
                    flex items-center justify-center text-deep-charcoal mb-6 shadow-soft">
                                        {experience.icon}
                                    </div>

                                    <h3 className="font-cormorant text-2xl text-deep-charcoal mb-4">
                                        {experience.title}
                                    </h3>
                                    <p className="font-poppins text-charcoal-60 leading-relaxed">
                                        {experience.description}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Personal Concierge CTA */}
            <section id="concierge" className="section-spacing">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="max-w-5xl mx-auto"
                    >
                        <div className="bg-gradient-to-br from-deep-charcoal via-charcoal-80 to-deep-charcoal
              rounded-3xl p-12 md:p-16 overflow-hidden relative"
                        >
                            {/* Background Pattern */}
                            <div className="absolute inset-0 opacity-10">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-champagne-gold to-transparent
                  rounded-full blur-3xl" />
                                <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-muted-sage to-transparent
                  rounded-full blur-3xl" />
                            </div>

                            <div className="relative z-10 text-center">
                                <GiPresent className="w-20 h-20 text-champagne-gold mx-auto mb-8" />

                                <h2 className="font-playfair text-5xl text-white mb-8">
                                    Bespoke Gifting Experience
                                </h2>

                                <p className="font-poppins text-xl text-charcoal-40 mb-12 max-w-3xl mx-auto leading-relaxed">
                                    Our luxury gifting concierge is dedicated to creating truly personalized experiences.
                                    From custom formulations to global white-glove delivery, we craft moments that transcend expectation.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <motion.button
                                        whileHover={{ scale: 1.05, y: -2 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="px-10 py-4 bg-gradient-to-r from-champagne-gold to-champagne-gold/80
                      text-deep-charcoal font-poppins rounded-full hover:shadow-xl
                      transition-all duration-300 flex items-center gap-3"
                                    >
                                        <FiShoppingBag className="w-5 h-5" />
                                        Schedule Concierge Consultation
                                    </motion.button>

                                    <motion.button
                                        whileHover={{ scale: 1.05, y: -2 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="px-10 py-4 bg-transparent border-2 border-champagne-gold/30
                      text-champagne-gold font-poppins rounded-full hover:border-champagne-gold
                      transition-all duration-300"
                                    >
                                        View Corporate Programs
                                    </motion.button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Closing Statement */}
            <section className="section-spacing">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="max-w-4xl mx-auto text-center"
                    >
                        <div className="relative">
                            {/* Decorative Elements */}
                            <div className="absolute -top-12 -left-12 w-24 h-24 border border-champagne-gold/10 rounded-full" />
                            <div className="absolute -bottom-12 -right-12 w-24 h-24 border border-champagne-gold/10 rounded-full" />

                            <div className="relative z-10">
                                <div className="inline-flex items-center gap-6 mb-12">
                                    <div className="h-px w-16 bg-gradient-to-r from-transparent to-champagne-gold" />
                                    <IoDiamondOutline className="w-8 h-8 text-champagne-gold" />
                                    <div className="h-px w-16 bg-gradient-to-l from-transparent to-champagne-gold" />
                                </div>

                                <h2 className="font-playfair text-5xl text-deep-charcoal mb-8">
                                    The Art of Extraordinary Gifting
                                </h2>
                                <p className="font-cormorant text-2xl text-charcoal-60 leading-relaxed">
                                    At KROMAIC, we believe the most meaningful gifts are those that create lasting impressions.
                                    Each collection is a testament to luxury, craftsmanship, and the art of thoughtful giving.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default GiftSetsPage;